"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";

type CirclePosition = {
  cx: number;
  cy: number;
  d: number;
};

type CircleFill =
  | { type: "image"; src: string }
  | { type: "color"; color: string };

const POSITIONS: CirclePosition[] = [
  { cx: 309.99, cy: 526.23, d: 84 },
  { cx: 129.34, cy: 519.55, d: 72 },
  { cx: 274.49, cy: 399.74, d: 72 },
  { cx: 93.68, cy: 363.64, d: 101 },
  { cx: 35.65, cy: 218.49, d: 72 },
  { cx: 223.73, cy: 232.57, d: 101 },
  { cx: 465.39, cy: 51.06, d: 101 },
  { cx: 254, cy: 66, d: 72 },
  { cx: 44.09, cy: 74.87, d: 72 },
  { cx: 431.09, cy: 371.58, d: 101 },
  { cx: 386.62, cy: 218.49, d: 72 },
];

const ALL_FILLS: CircleFill[] = [
  { type: "image", src: "/assets/images/users/user-0.webp" },
  { type: "color", color: "#CFFBFF" },
  { type: "color", color: "#EAE4E4" },
  { type: "image", src: "/assets/images/users/user-2.webp" },
  { type: "image", src: "/assets/images/users/user-3.webp" },
  { type: "color", color: "#D7FEE0" },
  { type: "color", color: "#DDE4EE" },
  { type: "image", src: "/assets/images/users/user-4.webp" },
  { type: "image", src: "/assets/images/users/user-5.webp" },
  { type: "image", src: "/assets/images/users/user-6.webp" },
  { type: "image", src: "/assets/images/users/user-1.webp" },
];

/** Pick n random unique indices from 0..max-1 */
function pickRandom(max: number, n: number): number[] {
  const indices = Array.from({ length: max }, (_, i) => i);
  for (let i = indices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [indices[i], indices[j]] = [indices[j], indices[i]];
  }
  return indices.slice(0, n);
}

/** Get a unique key for a fill to drive AnimatePresence */
function fillKey(fill: CircleFill, cycle: number): string {
  const base = fill.type === "color" ? fill.color : fill.src;
  return `${base}-${cycle}`;
}

const SVG_W = 516;
const SVG_H = 570;
const SWAP_COUNT = 3;
const SWAP_INTERVAL = 2500;

export function UsersCard() {
  const [fills, setFills] = useState(ALL_FILLS);
  const [cycle, setCycle] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setFills((prev) => {
        const next = [...prev];
        // Pick random positions to swap among each other
        const swapIndices = pickRandom(POSITIONS.length, SWAP_COUNT);

        // Rotate the fills at those positions:
        // index[0] gets index[1]'s fill, index[1] gets index[2]'s fill, etc.
        const firstFill = next[swapIndices[0]];
        for (let i = 0; i < swapIndices.length - 1; i++) {
          next[swapIndices[i]] = next[swapIndices[i + 1]];
        }
        next[swapIndices[swapIndices.length - 1]] = firstFill;

        return next;
      });
      setCycle((c) => c + 1);
    }, SWAP_INTERVAL);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return (
    <div className="relative w-full">
      <svg
        className="block w-full"
        viewBox={`0 0 ${SVG_W} ${SVG_H}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Dashed connector path */}
        <path
          d="M120.387 543.745C198.851 509.526 243.688 500.507 347.772 516.326C359.782 453.432 352.576 426.334 416.628 390.478L270.109 385.318C270.109 385.318 276.878 323.461 267.639 300.486C258.401 277.511 230.077 252.697 230.077 252.697C230.077 252.697 204.531 295.449 181.237 316.76C157.943 338.072 99.1699 361.84 99.1699 361.84C99.1699 361.84 104.023 309.622 89.5621 281.855C75.1009 254.087 36.7191 219.634 36.7191 219.634C36.7191 219.634 1.42769 101.157 55.1678 59.3353C107.951 18.2582 236.081 59.3353 236.081 59.3353C236.081 59.3353 295.879 77.1142 332.96 76.2088C382.187 75.0067 454.659 40.7041 454.659 40.7041C465.468 167.255 513.059 225.488 447.806 344.143C382.553 462.797 416.628 291.172 389.406 223.881"
          stroke="#BEBEBE"
          strokeWidth="0.674121"
          strokeDasharray="2.7 2.7"
        />

        {/* Animated circles */}
        {POSITIONS.map((pos, i) => {
          const fill = fills[i];
          const r = pos.d / 2;
          return (
            <foreignObject
              key={i}
              x={pos.cx - r}
              y={pos.cy - r}
              width={pos.d}
              height={pos.d}
            >
              <div className="relative h-full w-full">
                <AnimatePresence mode="popLayout">
                  <motion.div
                    key={fillKey(fill, cycle)}
                    className="absolute inset-0 overflow-hidden rounded-full"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.8, ease: "easeInOut" }}
                    style={{
                      backgroundColor:
                        fill.type === "color" ? fill.color : undefined,
                    }}
                  >
                    {fill.type === "image" && (
                      <Image
                        src={fill.src}
                        alt=""
                        width={200}
                        height={200}
                        className="h-full w-full object-cover"
                      />
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </foreignObject>
          );
        })}
      </svg>
    </div>
  );
}
